import type {
  History,
  LocationDescriptor,
  LocationDescriptorObject,
} from "history";

import { type MobileFunctions } from "./types";
import {
  emitWebviewNavigationEvent,
  handleWebviewBack,
} from "./webviewNavigationBridge";

/**
 * Whether `incoming` is a strict ancestor of `current`, compared at segment boundaries so
 * `/Bill/12` is not an ancestor of `/Bill/123`.
 */
function isAncestorPath(incoming: string, current: string): boolean {
  const normalize = (p: string) => p.replace(/[?#].*$/, "").replace(/\/+$/, "");
  const ancestor = normalize(incoming);
  const descendant = normalize(current);
  // Root is an ancestor of everything, but navigating to it is a deliberate reset rather
  // than a dropped segment.
  if (ancestor === "") {
    return false;
  }
  return ancestor !== descendant && descendant.startsWith(`${ancestor}/`);
}

function setupMobileNavigateFunctions(baseHistory: History) {
  const origin = window.location.origin;
  const basename = baseHistory.createHref({ pathname: "/" }).replace(/\/$/, "");

  // The URL from RN may be a full URL or a path that includes the basename (e.g., /app/).
  // Since baseHistory was created with a basename, we need to strip it to avoid
  // double-prefixing (e.g., /app/app/...). Also strip the origin if it's a full URL.
  // Hostnames are case-insensitive (RN may send a different casing than
  // window.location.origin), so compare case-insensitively but slice the original string.
  const stripPrefixes = (url: string): string => {
    let path = url;
    if (path.toLowerCase().startsWith(origin.toLowerCase())) {
      path = path.slice(origin.length);
    }
    if (
      basename &&
      path.toLowerCase().startsWith(basename.toLowerCase() + "/")
    ) {
      path = path.slice(basename.length);
    } else if (path.toLowerCase() === basename.toLowerCase()) {
      path = "/";
    }
    return path;
  };

  // Register a global callback that React Native can invoke via injectJavaScript
  // to navigate within the SPA without a full page reload. Uses baseHistory directly
  // (not shimmedHistory) to avoid re-emitting onNavigationEvent back to RN.
  (window as unknown as MobileFunctions)._webNavigate = (url, _type, state) => {
    const path = stripPrefixes(url);

    // Correcting an entry means normalising its URL, never changing which route is
    // mounted. Native rebuilds the URL from whichever route config it matched, so a
    // nested route resolving to its parent's config arrives shallower than where we
    // are - applying it would unmount what the web just navigated to, silently. Native
    // back goes through onGoBack, so nothing legitimate lands here as an ancestor.
    if (isAncestorPath(path, baseHistory.location.pathname)) {
      // Not dev-gated: this only fires when native breaks the contract above, and
      // failing silently is how it went unnoticed.
      console.warn(
        `[webview] ignoring _webNavigate("${path}") - it is an ancestor of the current ` +
          `path "${baseHistory.location.pathname}". Check the native route config whose ` +
          `url resolves to this path.`,
      );
      return;
    }

    // Always uses replace: by the time RN calls _webNavigate, the shimmed history
    // has already performed the original push/replace (it runs synchronously before
    // the async postMessage reaches RN). We're correcting the URL of the entry the
    // web already created, not adding a new one.
    baseHistory.replace(path, state);
  };
}

/**
 * Wraps a browser history object with webview navigation integration.
 *
 * - Emits `window.onNavigationEvent()` BEFORE routing occurs for push/replace
 * - Delegates all back navigation to native `window.onGoBack()`
 * - Registers `window._webNavigate` so React Native can navigate the SPA
 *   without a full page reload (uses baseHistory directly to avoid
 *   re-emitting onNavigationEvent back to RN)
 */
export function createWebviewHistoryWrapper(baseHistory: History): History {
  setupMobileNavigateFunctions(baseHistory);

  const normalizeLocation = (
    pathOrLocation: LocationDescriptor,
  ): LocationDescriptorObject => {
    if (typeof pathOrLocation === "string") {
      return { pathname: pathOrLocation };
    }
    return pathOrLocation;
  };

  const shimmedHistory: History = {
    ...baseHistory,

    push(pathOrLocation: LocationDescriptor, state?: unknown) {
      const location = normalizeLocation(pathOrLocation);
      if (state !== undefined) {
        location.state = state;
      }

      const url = baseHistory.createHref({
        pathname: location.pathname ?? "",
        search: location.search ?? "",
        hash: location.hash ?? "",
      });
      emitWebviewNavigationEvent(url, "push", location.state);

      return baseHistory.push(pathOrLocation, state);
    },

    replace(pathOrLocation: LocationDescriptor, state?: unknown) {
      const location = normalizeLocation(pathOrLocation);
      if (state !== undefined) {
        location.state = state;
      }

      const url = baseHistory.createHref({
        pathname: location.pathname ?? "",
        search: location.search ?? "",
        hash: location.hash ?? "",
      });
      emitWebviewNavigationEvent(url, "replace", location.state);

      return baseHistory.replace(pathOrLocation, state);
    },

    goBack() {
      if (!handleWebviewBack()) {
        baseHistory.goBack();
      }
    },

    go(n: number) {
      if (n < 0) {
        if (n < -1) {
          console.warn(
            `history.go(${n}) called in webview - only go(-1) is expected. Treating as single back navigation.`,
          );
        }
        if (!handleWebviewBack()) {
          baseHistory.go(n);
        }
        return;
      }
      return baseHistory.go(n);
    },
  };

  return shimmedHistory;
}
